import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from "react-redux";
import {
    List, ListItem, ListItemText, ListItemAvatar, Avatar, ToggleButtonGroup,
    ToggleButton, Box, Typography, TextField, Button, Grid, Paper,
    InputAdornment, CircularProgress
} from '@mui/material';
import GridViewIcon from '@mui/icons-material/GridView';
import ListIcon from '@mui/icons-material/List';
import SearchIcon from '@mui/icons-material/Search';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { FixedSizeList as VirtualList } from 'react-window';
import ContentPackCardComponent from '../../components/cards/ContentPackCard';
import { useGetCoursesQuery } from "../../store/services/university-service";
import { APP_URL } from '../../config';
import { getCardColor, getTextColor } from "../../utils/colorUtils";
import ContentPackCoursePreviewModal from './ContentPackCoursePreviewModal';
import { ModalProvider } from '../../components/Modal/ModalContext';

const EmptyStateContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    padding: theme.spacing(3),
    textAlign: 'center',
}));

const EmptyStateIcon = styled(Box)(({ theme }) => ({
    color: theme.palette.action.disabled,
    marginBottom: theme.spacing(2),
    '& svg': {
        fontSize: 64,
    },
}));

const DroppableSection = styled(Paper)(({ theme, isDragOver }) => ({
    height: 700,
    minHeight: 700,
    padding: theme.spacing(2),
    backgroundColor: isDragOver ? getCardColor(theme.palette.mode === 'light') : getCardColor(theme.palette.mode === 'light', false),
    border: `1px solid ${isDragOver ? theme.palette.primary.main : theme.palette.divider}`,
    borderRadius: theme.borderRadius.sm,
    boxShadow: theme.customShadows.z1,
    transition: 'all 0.2s ease',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const ContentArea = styled(Box)({
    flexGrow: 1,
    overflow: 'hidden',
    position: 'relative',
    minHeight: 0,
});

const SearchContainer = styled(Box)({
    flexGrow: 1,
});

const CourseListItem = styled(ListItem)(({ theme, isDragging }) => ({
    backgroundColor: getCardColor(theme.palette.mode === 'light', false),
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: theme.borderRadius.sm,
    marginBottom: theme.spacing(1),
    opacity: isDragging ? 0.5 : 1,
    cursor: 'move',
    padding: theme.spacing(1, 2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:hover': {
        backgroundColor: getCardColor(theme.palette.mode === 'light'),
    },
    '& img': {
        pointerEvents: 'none',
    },
}));

const ListItemContent = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
});

const PreviewButton = styled(Button)(({ theme }) => ({
    marginLeft: theme.spacing(2),
    minWidth: '100px',
}));

// Optimized drag preview component
const DragPreview = ({ show, x, y, course, theme }) => {
    const previewRef = useRef(null);

    useEffect(() => {
        if (previewRef.current && show) {
            previewRef.current.style.transform = `translate3d(${x - 100}px, ${y - 50}px, 0)`;
        }
    }, [x, y, show]);

    if (!show || !course) return null;

    return (
        <Box
            ref={previewRef}
            sx={{
                position: 'fixed',
                left: 0,
                top: 0,
                transform: 'translate3d(-100px, -50px, 0)',
                opacity: 0.7,
                pointerEvents: 'none',
                zIndex: theme.zIndex.tooltip + 1,
                willChange: 'transform',
                width: 200,
            }}
        >
            <ContentPackCardComponent
                image={`${APP_URL}/${course.courseContentPath.replace('index.html', 'card.png')}`}
                gif={`${APP_URL}/${course.courseContentPath.replace('index.html', 'card.gif')}`}
                title={course.courseName}
                type="preview"
            />
        </Box>
    );
};

// Memoized virtualized list item for grid view (renders 2 cards per row)
const VirtualizedGridItem = React.memo(({
    index,
    style,
    data: { courses, draggedCourse, onDragStart, onDrag, onDragEnd, onPreview }
}) => {
    const leftIndex = index * 2;
    const rightIndex = leftIndex + 1;
    const leftCourse = courses[leftIndex];
    const rightCourse = courses[rightIndex];

    const leftIsDragging = draggedCourse?.course.id === leftCourse?.id;
    const rightIsDragging = draggedCourse?.course.id === rightCourse?.id;

    return (
        <div style={style}>
            <Grid container spacing={2} sx={{ p: 1 }}>
                {/* Left Course */}
                <Grid item xs={6}>
                    {leftCourse && (
                        <Box
                            draggable
                            onDragStart={(e) => onDragStart(e, leftCourse)}
                            onDrag={onDrag}
                            onDragEnd={onDragEnd}
                            sx={{
                                opacity: leftIsDragging ? 0.5 : 1,
                                cursor: 'move',
                                transition: 'all 0.2s ease',
                                transform: leftIsDragging ? 'scale(1.02)' : 'scale(1)',
                            }}
                        >
                            <ContentPackCardComponent
                                image={`${APP_URL}/${leftCourse.courseContentPath.replace('index.html', 'card.png')}`}
                                gif={`${APP_URL}/${leftCourse.courseContentPath.replace('index.html', 'card.gif')}`}
                                title={leftCourse.courseName}
                                type="preview"
                                onClick={() => onPreview(leftCourse.id)}
                            />
                        </Box>
                    )}
                </Grid>

                {/* Right Course */}
                <Grid item xs={6}>
                    {rightCourse && (
                        <Box
                            draggable
                            onDragStart={(e) => onDragStart(e, rightCourse)}
                            onDrag={onDrag}
                            onDragEnd={onDragEnd}
                            sx={{
                                opacity: rightIsDragging ? 0.5 : 1,
                                cursor: 'move',
                                transition: 'all 0.2s ease',
                                transform: rightIsDragging ? 'scale(1.02)' : 'scale(1)',
                            }}
                        >
                            <ContentPackCardComponent
                                image={`${APP_URL}/${rightCourse.courseContentPath.replace('index.html', 'card.png')}`}
                                gif={`${APP_URL}/${rightCourse.courseContentPath.replace('index.html', 'card.gif')}`}
                                title={rightCourse.courseName}
                                type="preview"
                                onClick={() => onPreview(rightCourse.id)}
                            />
                        </Box>
                    )}
                </Grid>
            </Grid>
        </div>
    );
});

// Memoized virtualized list item for list view
const VirtualizedListItem = React.memo(({
    index,
    style,
    data: { courses, draggedCourse, onDragStart, onDrag, onDragEnd, onPreview, theme }
}) => {
    const course = courses[index];
    const isDragging = draggedCourse?.course.id === course.id;

    return (
        <div style={style}>
            <CourseListItem
                draggable="true"
                onDragStart={(e) => onDragStart(e, course)}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
                isDragging={isDragging}
                sx={{ margin: '4px 0' }}
            >
                <ListItemContent>
                    <ListItemAvatar>
                        <Avatar
                            variant="rounded"
                            src={`${APP_URL}/${course.courseContentPath.replace('index.html', 'card.png')}`}
                            alt={course.courseName}
                            sx={{ width: 56, height: 56, pointerEvents: 'none' }}
                            draggable="false"
                        />
                    </ListItemAvatar>
                    <ListItemText primary={course.courseName} sx={{ ml: 2 }} />
                </ListItemContent>
                <PreviewButton
                    variant="contained"
                    color={theme.palette.mode === 'light' ? "warning" : "primary"}
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onPreview(course.id);
                    }}
                    sx={{
                        ...(theme.palette.mode !== 'light' && {
                            bgcolor: theme.palette.primary.main,
                            color: getTextColor(theme.palette.primary.main),
                            '&:hover': {
                                bgcolor: "#69686b",
                            },
                        }),
                    }}
                >
                    Preview
                </PreviewButton>
            </CourseListItem>
        </div>
    );
});

const CourseSection = React.memo(({
    title,
    courses,
    onDrop,
    sectionId,
    draggedCourse,
    setDraggedCourse,
    isDragOver,
    setIsDragOver,
    showSearch = false,
    onPreview,
    count
}) => {
    const theme = useTheme();
    const [viewMode, setViewMode] = useState(sectionId === 'available' ? 'list' : 'grid');
    const [searchQuery, setSearchQuery] = useState('');
    const [dragPreview, setDragPreview] = useState({ show: false, x: 0, y: 0, course: null });

    // New state and ref for dynamic height measurement
    const [containerHeight, setContainerHeight] = useState(500);
    const contentAreaRef = useRef(null);

    // Use RAF for smoother drag tracking
    const rafRef = useRef();
    const isDraggingRef = useRef(false);

    // Dynamic height measurement
    useEffect(() => {
        const measureHeight = () => {
            if (contentAreaRef.current) {
                const rect = contentAreaRef.current.getBoundingClientRect();
                setContainerHeight(rect.height);
            }
        };

        // Initial measurement
        measureHeight();

        // Add resize listener
        window.addEventListener('resize', measureHeight);

        // Use ResizeObserver if available for more accurate measurements
        let resizeObserver;
        if (window.ResizeObserver && contentAreaRef.current) {
            resizeObserver = new ResizeObserver(measureHeight);
            resizeObserver.observe(contentAreaRef.current);
        }

        return () => {
            window.removeEventListener('resize', measureHeight);
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
        };
    }, []);

    const filteredCourses = useMemo(() =>
        courses.filter(course =>
            course.courseName.toLowerCase().includes(searchQuery.toLowerCase())
        ), [courses, searchQuery]
    );

    const handleDragStart = useCallback((e, course) => {
        e.stopPropagation();

        const dragImage = document.createElement('div');
        dragImage.style.opacity = '0';
        document.body.appendChild(dragImage);
        e.dataTransfer.setDragImage(dragImage, 0, 0);

        setTimeout(() => document.body.removeChild(dragImage), 0);

        isDraggingRef.current = true;
        setDraggedCourse({ course, sectionId, sourceViewMode: viewMode });
        setDragPreview({ show: true, x: e.clientX, y: e.clientY, course });
    }, [sectionId, viewMode, setDraggedCourse]);

    const handleDrag = useCallback((e) => {
        if (e.clientX === 0 && e.clientY === 0 || !isDraggingRef.current) return;

        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
        }

        rafRef.current = requestAnimationFrame(() => {
            if (isDraggingRef.current) {
                setDragPreview(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
            }
        });
    }, []);

    const handleDragEnd = useCallback(() => {
        isDraggingRef.current = false;
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current);
        }
        setDragPreview({ show: false, x: 0, y: 0, course: null });
        setDraggedCourse(null);
        setIsDragOver(null);
    }, [setDraggedCourse, setIsDragOver]);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        if (!isDragOver) setIsDragOver(sectionId);
    }, [isDragOver, sectionId, setIsDragOver]);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        if (e.currentTarget.contains(e.relatedTarget)) return;
        setIsDragOver(null);
    }, [setIsDragOver]);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        setIsDragOver(null);
        if (draggedCourse && draggedCourse.sectionId !== sectionId) {
            onDrop(draggedCourse.course.id, draggedCourse.sectionId, sectionId);
        }
        setDraggedCourse(null);
        setDragPreview({ show: false, x: 0, y: 0, course: null });
    }, [draggedCourse, sectionId, onDrop, setDraggedCourse, setIsDragOver]);

    const renderEmptyState = () => {
        if (searchQuery) {
            return (
                <EmptyStateContainer>
                    <EmptyStateIcon><SearchOffIcon /></EmptyStateIcon>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                        No Matching Courses Found
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        We couldn't find any courses matching "{searchQuery}"
                    </Typography>
                    <Button variant="text" onClick={() => setSearchQuery('')} sx={{ mt: 2 }}>
                        Clear Search
                    </Button>
                </EmptyStateContainer>
            );
        }

        return (
            <EmptyStateContainer>
                <EmptyStateIcon><FormatListBulletedIcon /></EmptyStateIcon>
                <Typography variant="h6" color="textSecondary" gutterBottom>
                    {sectionId === 'available' ? 'No Available Courses' : 'No Selected Courses'}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                    {sectionId === 'available'
                        ? 'There are currently no courses available to add to your content pack.'
                        : 'Start building your content pack by dragging courses from the available section.'}
                </Typography>
            </EmptyStateContainer>
        );
    };

    const itemData = useMemo(() => ({
        courses: filteredCourses,
        draggedCourse,
        onDragStart: handleDragStart,
        onDrag: handleDrag,
        onDragEnd: handleDragEnd,
        onPreview,
        theme
    }), [filteredCourses, draggedCourse, handleDragStart, handleDrag, handleDragEnd, onPreview, theme]);

    const renderVirtualizedContent = () => {
        if (filteredCourses.length === 0) {
            return renderEmptyState();
        }

        const itemHeight = viewMode === 'grid' ? 280 : 90;
        // For grid view, calculate rows needed (2 cards per row)
        const itemCount = viewMode === 'grid'
            ? Math.ceil(filteredCourses.length / 2)
            : filteredCourses.length;

        // Use the measured container height with a small buffer for safety
        const safeHeight = Math.max(containerHeight - 10, 100); // Minimum 100px height
        const height = Math.min(safeHeight, itemCount * itemHeight);

        return (
            <VirtualList
                height={height}
                itemCount={itemCount}
                itemSize={itemHeight}
                itemData={itemData}
                overscanCount={5}
            >
                {viewMode === 'grid' ? VirtualizedGridItem : VirtualizedListItem}
            </VirtualList>
        );
    };

    return (
        <>
            <DroppableSection
                className="content-area"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                isDragOver={isDragOver === sectionId}
            >
                <Typography variant="h4" sx={{ pb: 2 }}>{title} ({count})</Typography>

                <ControlsContainer>
                    {showSearch ? (
                        <SearchContainer>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Search courses..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </SearchContainer>
                    ) : (
                        <Box flexGrow={1} />
                    )}
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(e, newView) => newView && setViewMode(newView)}
                        size="small"
                    >
                        <ToggleButton value="grid"><GridViewIcon /></ToggleButton>
                        <ToggleButton value="list"><ListIcon /></ToggleButton>
                    </ToggleButtonGroup>
                </ControlsContainer>

                <ContentArea ref={contentAreaRef}>
                    {renderVirtualizedContent()}
                </ContentArea>
            </DroppableSection>

            {dragPreview.show && draggedCourse?.sectionId === sectionId && (
                <DragPreview
                    show={dragPreview.show}
                    x={dragPreview.x}
                    y={dragPreview.y}
                    course={dragPreview.course}
                    theme={theme}
                />
            )}
        </>
    );
});

const ContentPackForm = ({ initialData, onSave }) => {
    const [selectedCourses, setSelectedCourses] = useState(initialData?.courseIds || []);
    const [draggedCourse, setDraggedCourse] = useState(null);
    const [isDragOver, setIsDragOver] = useState(null);
    const { tenancyId } = useSelector((state) => state.tenancy);
    const [previewModal, setPreviewModal] = useState({
        open: false,
        courseId: null
    });

    const user = JSON.parse(localStorage.getItem('user'));
    const additionalQueryParams = user?.role === "Administrators"
        ? ""
        : `&published=true&tenancyId=${tenancyId}`;

    const { data: allCourses = [], isLoading } = useGetCoursesQuery({
        perPage: 1000,
        page: 0,
        includes: '',
        query: '',
        additionalQueryParams: additionalQueryParams
    });

    const availableCourses = useMemo(() =>
        allCourses.filter(course => !selectedCourses.includes(course.id)),
        [allCourses, selectedCourses]
    );

    const selectedCourseObjects = useMemo(() =>
        allCourses.filter(course => selectedCourses.includes(course.id)),
        [allCourses, selectedCourses]
    );

    const handleDrop = useCallback((courseId, sourceType, targetType) => {
        if (sourceType === targetType) return;

        const newSelectedCourses = targetType === 'selected'
            ? [...selectedCourses, courseId]
            : selectedCourses.filter(id => id !== courseId);

        setSelectedCourses(newSelectedCourses);
        onSave({ content: newSelectedCourses });
    }, [selectedCourses, onSave]);

    const handleCoursePreview = useCallback((courseId) => {
        setPreviewModal({
            open: true,
            courseId
        });
    }, []);

    const handleClosePreview = useCallback(() => {
        setPreviewModal({
            open: false,
            courseId: null
        });
    }, []);

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading courses...</Typography>
            </Box>
        );
    }

    return (
        <>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <CourseSection
                        title="Available Courses"
                        courses={availableCourses}
                        onDrop={handleDrop}
                        sectionId="available"
                        draggedCourse={draggedCourse}
                        setDraggedCourse={setDraggedCourse}
                        isDragOver={isDragOver}
                        setIsDragOver={setIsDragOver}
                        showSearch={true}
                        onPreview={handleCoursePreview}
                        count={availableCourses.length}
                    />
                </Grid>
                <Grid item xs={6}>
                    <CourseSection
                        title="Content Pack"
                        courses={selectedCourseObjects}
                        onDrop={handleDrop}
                        sectionId="selected"
                        draggedCourse={draggedCourse}
                        setDraggedCourse={setDraggedCourse}
                        isDragOver={isDragOver}
                        setIsDragOver={setIsDragOver}
                        showSearch={false}
                        onPreview={handleCoursePreview}
                        count={selectedCourses.length}
                    />
                </Grid>
            </Grid>
            <ModalProvider>
                <ContentPackCoursePreviewModal
                    courseId={previewModal.courseId}
                    open={previewModal.open}
                    onClose={handleClosePreview}
                    isStandalone={true}
                />
            </ModalProvider>
        </>
    );
};

ContentPackForm.propTypes = {
    initialData: PropTypes.object.isRequired,
    onSave: PropTypes.func.isRequired,
    mode: PropTypes.string.isRequired,
    isSaving: PropTypes.bool,
};

export default ContentPackForm;